/**
 * @file waiterReservations.service.ts
 * @description Dedicated service for fetching and updating venue table reservations.
 */

import { supabase } from '../../../config/supabase.config';
import type { WaiterReservation, WaiterReservationStatus } from '../../../types/reservation.types';

export const fetchWaiterReservations = async (venueId: string): Promise<WaiterReservation[]> => {
  // Operational shift window: start of current day minus 4 hours to accommodate late-night shifts
  const shiftWindowStart = new Date(new Date().setHours(0, 0, 0, 0) - 4 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('reservations')
    // Uses party_size and native contact fields matching the database schema
    .select(`
      id,
      venue_id,
      table_id,
      party_size,
      guest_name,
      phone,
      email,
      start_time,
      end_time,
      status,
      special_requests,
      notes,
      created_at,
      customer:customers (
        name,
        phone,
        email
      )
    `)
    .eq('venue_id', venueId)
    .gte('start_time', shiftWindowStart)
    .order('start_time', { ascending: true });

  if (error) {
    console.error('[waiterReservations.service] Fetch error:', error);
    throw error;
  }

  return (data || []).map((row: any): WaiterReservation => {
    const meta = row.metadata || {};
    const customer = row.customer || {};

    const customerName =
      row.guest_name ||
      customer.name ||
      meta.customer_name ||
      meta.guest_name ||
      'Guest';

    const customerPhone = row.phone || customer.phone || meta.customer_phone || meta.phone || null;
    const customerEmail = row.email || customer.email || meta.customer_email || meta.email || null;
    const tableNum = meta.table_number || row.table_id || null;

    return {
      id: row.id,
      venue_id: row.venue_id,
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: customerEmail,
      party_size: Number(row.party_size) || Number(meta.party_size) || 2,
      table_id: row.table_id || null,
      table_number: tableNum ? String(tableNum) : null,
      start_time: row.start_time,
      end_time: row.end_time || null,
      status: (row.status as WaiterReservationStatus) || 'confirmed',
      special_requests: row.special_requests || meta.notes || null,
      created_at: row.created_at,
    };
  });
};

export const updateWaiterReservationStatus = async (
  reservationId: string,
  newStatus: WaiterReservationStatus,
  reason?: string
): Promise<void> => {
  // 1. Primary path: trigger state transition RPC to record timestamps, table occupancy, and audit logs
  try {
    const { data, error: rpcError } = await (supabase.rpc as any)('transition_reservation_status', {
      p_reservation_id: reservationId,
      p_new_status: newStatus,
      p_reason: reason || null,
    });

    if (!rpcError && data && data.success !== false) {
      return;
    }
  } catch (err) {
    console.warn('[waiterReservations.service] RPC transition failed, falling back to direct update:', err);
  }

  // 2. Direct fallback update if RPC is restricted or unavailable
  const now = new Date().toISOString();
  const directUpdates: Record<string, any> = {
    status: newStatus,
    updated_at: now,
  };
  if (newStatus === 'seated') directUpdates.seated_at = now;
  if (newStatus === 'cancelled') directUpdates.cancelled_at = now;
  if (newStatus === 'completed') directUpdates.completed_at = now;

  const { error } = await supabase
    .from('reservations')
    .update(directUpdates)
    .eq('id', reservationId);

  if (error) {
    console.error('[waiterReservations.service] Update status error:', error);
    throw error;
  }
};
